import { useCallback, useEffect, useRef, useState } from 'react';
import { MANUAL_HIGHLIGHTS_ENABLED, setLinesHighlight, parseHighlightMarker, setLineDone, isDoneLine } from '../lib/highlightMarkers';

/**
 * useLineHighlight — Walter 2026-06-20. Drives the Ctrl+H manual line
 * highlights for an editor (ListPanel / FocusedPanel).
 *
 * Ctrl+H applies the LAST-used color to the caret's line(s) and pops a small
 * palette by the caret; 1/2/3 (or a click) switch color, the active color
 * again / 0 / ✕ removes. The content change is committed through the editor's
 * own pipeline via `commit(nextValue, selStart, selEnd)` so save / lock /
 * selection-restore all stay consistent — the same shape as the Alt+Shift
 * move-line handler.
 *
 * The editor wires `onKeyDown` into its textarea and renders <HighlightPopup>
 * from `popup` when non-null.
 */

// Measure viewport coords just below the caret, for anchoring the popup.
// Mirror-div technique (same idea as FindBar's measureMatchTop), extended to
// also read the caret's x.
function caretCoords(ta) {
  const rect = ta.getBoundingClientRect();
  const lineH = parseFloat(window.getComputedStyle(ta).lineHeight) || 18;
  try {
    const cs = window.getComputedStyle(ta);
    const div = document.createElement('div');
    const s = div.style;
    s.position = 'absolute'; s.visibility = 'hidden'; s.left = '-9999px'; s.top = '0';
    s.boxSizing = 'border-box'; s.width = `${ta.clientWidth}px`;
    [
      'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'letterSpacing',
      'lineHeight', 'textTransform', 'wordSpacing', 'textIndent', 'whiteSpace',
      'wordBreak', 'overflowWrap', 'tabSize',
      'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    ].forEach((k) => { s[k] = cs[k]; });
    if (!s.whiteSpace || s.whiteSpace === 'normal') s.whiteSpace = 'pre-wrap';
    div.textContent = (ta.value || '').slice(0, ta.selectionStart);
    const marker = document.createElement('span');
    marker.textContent = '​';
    div.appendChild(marker);
    document.body.appendChild(div);
    const top = marker.offsetTop;
    const left = marker.offsetLeft;
    document.body.removeChild(div);
    const x = rect.left + Math.max(8, Math.min(left, ta.clientWidth - 130));
    const y = rect.top + (top - ta.scrollTop) + lineH + 6;
    // Clamp into the viewport.
    return {
      x: Math.max(8, Math.min(x, window.innerWidth - 140)),
      y: Math.max(8, Math.min(y, window.innerHeight - 48)),
    };
  } catch {
    return { x: rect.left + 12, y: rect.top + 28 };
  }
}

export default function useLineHighlight({
  textareaRef, getContent, isReminderList, commit, readOnly, pushToast,
}) {
  const [popup, setPopup] = useState(null); // { x, y, active, sel, fading } | null
  // Auto-fade timers (Walter 2026-06-22: pop fades after ~1.5s idle).
  const fadeRef = useRef({ t1: null, t2: null });
  const clearFade = useCallback(() => {
    clearTimeout(fadeRef.current.t1);
    clearTimeout(fadeRef.current.t2);
  }, []);
  // (Re)start the fade countdown — kept ALIVE by each Ctrl+H cycle.
  const armFade = useCallback(() => {
    clearFade();
    fadeRef.current.t1 = setTimeout(() => setPopup((p) => (p ? { ...p, fading: true } : p)), 1200);
    fadeRef.current.t2 = setTimeout(() => setPopup(null), 1500);
  }, [clearFade]);
  useEffect(() => () => clearFade(), [clearFade]); // clear on unmount

  // Apply a color (0 = remove) to a specific [start,end] selection.
  const apply = useCallback((colorIndex, sel) => {
    if (readOnly) return null;
    const ta = textareaRef.current;
    if (!ta) return null;
    const start = sel ? sel.start : ta.selectionStart;
    const end = sel ? sel.end : ta.selectionEnd;
    const res = setLinesHighlight(getContent(), start, end, colorIndex, { isReminderList });
    if (res.skipped) {
      pushToast?.('highlights aren’t available on reminder or header lines yet', null, { ttlMs: 3500 });
      return res;
    }
    if (res.changed) commit(res.nextValue, res.nextSelStart, res.nextSelEnd);
    return res;
  }, [textareaRef, getContent, isReminderList, commit, readOnly, pushToast]);

  const close = useCallback(() => { clearFade(); setPopup(null); }, [clearFade]);

  // Ctrl+Shift+H — "mark as done": grey out the caret's line(s) by toggling a
  // leading `[x] ` marker (Walter 2026-06-23). Sibling stroke to Ctrl+H so
  // there's no new key to learn. No popup — it's a binary toggle. For a
  // multi-line selection: if any line isn't done, mark all done; else clear.
  const toggleDone = useCallback(() => {
    if (readOnly) return;
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const content = getContent();
    const lineStart = content.lastIndexOf('\n', start - 1) + 1;
    let lineEnd = content.indexOf('\n', lineStart);
    if (lineEnd === -1) lineEnd = content.length;
    const curDone = isDoneLine(content.slice(lineStart, lineEnd));
    const res = setLineDone(content, start, end, !curDone, { isReminderList });
    if (res.skipped) {
      pushToast?.('“done” isn’t available on reminder or header lines yet', null, { ttlMs: 3000 });
      return;
    }
    if (res.changed) {
      close(); // a colour popup, if open, is now moot
      commit(res.nextValue, res.nextSelStart, res.nextSelEnd);
    }
  }, [textareaRef, getContent, isReminderList, commit, readOnly, pushToast, close]);

  // Walter 2026-06-22: Ctrl+H now just CYCLES the line's highlight —
  // none → green → blue → red → none. No numbers, no arrows; the popup is a
  // passive legend showing where you are. Any other keystroke or click closes
  // it. Reads the line's CURRENT color each press so the cycle is stateless
  // (no remembered "last color"). Acts on the line you opened on (stored sel).
  const cycle = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    const sel = popup ? popup.sel : { start: ta.selectionStart, end: ta.selectionEnd };
    const content = getContent();
    const lineStart = content.lastIndexOf('\n', sel.start - 1) + 1;
    let lineEnd = content.indexOf('\n', lineStart);
    if (lineEnd === -1) lineEnd = content.length;
    const cur = parseHighlightMarker(content.slice(lineStart, lineEnd))?.colorIndex || 0;
    const next = (cur + 1) % 4; // 0 none → 1 → 2 → 3 → 0
    const res = apply(next, sel);
    if (!res || res.skipped) return; // ineligible line → no popup
    if (popup) {
      setPopup({ ...popup, active: next || null, fading: false });
    } else {
      const pos = caretCoords(ta);
      setPopup({ x: pos.x, y: pos.y, active: next || null, sel, fading: false });
    }
    armFade();
  }, [textareaRef, popup, getContent, apply, armFade]);

  const onKeyDown = useCallback((e) => {
    if (!MANUAL_HIGHLIGHTS_ENABLED) return false;
    // Walter 2026-07-04: Ctrl only — NOT Cmd. On macOS ⌘H is the system
    // "Hide Window" shortcut, so binding metaKey here hijacked it (Jeffrey:
    // "Ctrl-H does something weird on Mac"). Ctrl+H works cross-platform; the
    // preventDefault below blocks macOS's Ctrl+H "delete backward" text binding.
    const mod = e.ctrlKey && !e.metaKey && !e.altKey;
    const isH = e.key === 'h' || e.key === 'H';
    // Ctrl+Shift+H → mark-as-done (checked BEFORE plain Ctrl+H so the shift
    // variant doesn't fall through to the colour cycle).
    if (mod && isH && e.shiftKey) { e.preventDefault(); toggleDone(); return true; }
    if (mod && isH) { e.preventDefault(); cycle(); return true; }
    if (popup) {
      // Enter / Tab / Esc lock in + dismiss without editing the line (Enter
      // would otherwise add a newline / replace a selection).
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
        e.preventDefault();
        close();
        const ta = textareaRef.current;
        if (ta) {
          const p = ta.selectionEnd;
          requestAnimationFrame(() => { try { ta.setSelectionRange(p, p); } catch { /* noop */ } });
        }
        return true;
      }
      // Any other real key closes the popup (and is allowed through).
      if (!['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) close();
    }
    return false;
  }, [popup, cycle, toggleDone, close, textareaRef]);

  // Any click closes the popup (it's a passive legend — pointer-events:none).
  useEffect(() => {
    if (!popup) return undefined;
    const onDown = () => { clearFade(); setPopup(null); };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [popup, clearFade]);

  return { onKeyDown, popup, close };
}
